import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService, showToast } from './api.service';

interface TrainingSession {
  id: number;
  name: string;
  description?: string | null;
  status: 'Draft' | 'Active' | 'Disabled';
}

interface TrainingSample {
  id: number;
  session_id: number;
  ai_job_id: string;
  title: string;
  status: string;
  source_image_url?: string | null;
  job_source_image_url?: string | null;
  ai_status?: string | null;
  ai_doc_status?: string | null;
  ai_message?: string | null;
  submitted_at?: string | null;
  reviewed_at?: string | null;
  created_at?: string | null;
}

@Component({
  selector: 'app-admin-as-built-training-session',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './admin-as-built-training-session.component.html',
  styleUrl: './admin-as-built-training.component.css'
})
export class AdminAsBuiltTrainingSessionComponent {
  private api = inject(ApiService);
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  session = signal<TrainingSession | null>(null);
  samples = signal<TrainingSample[]>([]);
  isLoading = signal(false);
  isUploading = signal(false);
  uploadTitle = signal('');
  uploadNotes = signal('');
  uploadFile = signal<File | null>(null);
  uploadPreview = signal<string | null>(null);

  private poll: any;

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const id = Number(params.get('sessionId'));
      if (Number.isFinite(id) && id > 0) {
        this.loadSession(id);
        if (this.poll) clearInterval(this.poll);
        this.poll = setInterval(() => this.loadSession(id, true), 10000);
      } else {
        this.router.navigate(['/admin/ai/as-built/training']);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.poll) clearInterval(this.poll);
  }

  goBack(): void {
    this.router.navigate(['/admin/ai/as-built/training']);
  }

  loadSession(id = this.session()?.id, silent = false): void {
    if (!id) return;
    if (!silent) this.isLoading.set(true);
    this.http.get<any>(`${this.api.baseUrl}/ai/get_as_built_training_session.php?id=${id}`, { headers: (this.api as any).getHeaders() })
      .subscribe({
        next: (res) => {
          this.isLoading.set(false);
          if (res?.success) {
            this.session.set(res.data.session);
            this.samples.set(res.data.samples || []);
          }
        },
        error: () => {
          this.isLoading.set(false);
          if (!silent) showToast('error', 'Could not load training set');
        }
      });
  }

  chooseFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.uploadFile.set(file);
    this.uploadPreview.set(file ? URL.createObjectURL(file) : null);
  }

  addPlan(): void {
    const session = this.session();
    const file = this.uploadFile();
    if (!session || !file) {
      showToast('error', 'Choose a plan image first');
      return;
    }

    this.isUploading.set(true);
    const formData = new FormData();
    formData.append('plan_image', file);
    formData.append('title', this.uploadTitle() || file.name.replace(/\.[^.]+$/, ''));

    this.http.post<any>(`${this.api.baseUrl}/ai/extract_drainage.php`, formData, {
      headers: (this.api as any).getHeaders().delete('Content-Type')
    }).subscribe({
      next: (res) => {
        if (!res?.success || !res.data?.job_id) {
          this.isUploading.set(false);
          showToast('error', res?.message || 'Could not start AI drawing');
          return;
        }
        this.createSample(session.id, res.data.job_id, formData.get('title') as string);
      },
      error: (e) => {
        this.isUploading.set(false);
        showToast('error', e.error?.message || 'Could not upload plan');
      }
    });
  }

  openEditor(sample: TrainingSample): void {
    if (!sample.ai_job_id) return;
    if (sample.status === 'Processing' || sample.ai_status === 'processing') {
      showToast('error', 'The AI drawing is still being prepared');
      return;
    }
    this.router.navigate(['/admin/ai/as-built', sample.ai_job_id], {
      queryParams: { trainingSessionId: this.session()?.id, trainingSampleId: sample.id }
    });
  }

  approve(sample: TrainingSample, event: Event): void {
    event.stopPropagation();
    this.updateSampleStatus(sample, 'Approved');
  }

  reject(sample: TrainingSample, event: Event): void {
    event.stopPropagation();
    this.updateSampleStatus(sample, 'Rejected');
  }

  deleteSample(sample: TrainingSample, event: Event): void {
    event.stopPropagation();
    if (!confirm(`Delete "${sample.title || 'training sample'}"?`)) return;
    this.http.post<any>(`${this.api.baseUrl}/ai/delete_as_built_training_session_sample.php`, {
      sample_id: sample.id
    }, { headers: (this.api as any).getHeaders() }).subscribe({
      next: (res) => {
        if (res?.success) {
          showToast('success', 'Training sample deleted');
          this.loadSession();
        } else {
          showToast('error', res?.message || 'Delete failed');
        }
      },
      error: () => showToast('error', 'Delete failed')
    });
  }

  setSessionStatus(status: TrainingSession['status']): void {
    const session = this.session();
    if (!session) return;
    this.http.post<any>(`${this.api.baseUrl}/ai/save_as_built_training_session.php`, {
      ...session,
      status
    }, { headers: (this.api as any).getHeaders() }).subscribe({
      next: (res) => {
        if (res?.success) {
          showToast('success', status === 'Active' ? 'Training set turned on' : 'Training set updated');
          this.loadSession(session.id);
        } else {
          showToast('error', res?.message || 'Could not update training set');
        }
      },
      error: () => showToast('error', 'Could not update training set')
    });
  }

  imageHref(sample: TrainingSample): string {
    return this.api.assetUrl(sample.source_image_url || sample.job_source_image_url || '');
  }

  statusLabel(sample: TrainingSample): string {
    if (sample.ai_status === 'processing') return 'Processing';
    if (sample.ai_status === 'error') return 'Failed';
    return sample.status === 'Uploaded' ? 'Processing' : sample.status;
  }

  formatDate(value?: string | null): string {
    if (!value) return '';
    try {
      return new Date(value).toLocaleDateString('en-NZ', { timeZone: 'Pacific/Auckland' });
    } catch {
      return value;
    }
  }

  private createSample(sessionId: number, jobId: string, title: string): void {
    this.http.post<any>(`${this.api.baseUrl}/ai/create_as_built_training_sample.php`, {
      session_id: sessionId,
      job_id: jobId,
      title,
      notes: this.uploadNotes()
    }, { headers: (this.api as any).getHeaders() }).subscribe({
      next: (res) => {
        this.isUploading.set(false);
        if (res?.success) {
          this.uploadTitle.set('');
          this.uploadNotes.set('');
          this.uploadFile.set(null);
          this.uploadPreview.set(null);
          showToast('success', 'Plan added. AI drawing is being prepared');
          this.loadSession(sessionId);
        } else {
          showToast('error', res?.message || 'Could not add sample');
        }
      },
      error: () => {
        this.isUploading.set(false);
        showToast('error', 'Could not add sample');
      }
    });
  }

  private updateSampleStatus(sample: TrainingSample, status: 'Approved' | 'Rejected' | 'Ready for Edit'): void {
    this.http.post<any>(`${this.api.baseUrl}/ai/update_as_built_training_sample_status.php`, {
      sample_id: sample.id,
      status
    }, { headers: (this.api as any).getHeaders() }).subscribe({
      next: (res) => {
        if (res?.success) {
          showToast('success', 'Training sample updated');
          this.loadSession();
        } else {
          showToast('error', res?.message || 'Could not update sample');
        }
      },
      error: () => showToast('error', 'Could not update sample')
    });
  }
}
